import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CalendarOff, Plus, TriangleAlert, Trash2, X } from "lucide-react";
import type {
  DispatchRun,
  DispatchRunSchedule,
  RunScheduleWeekday,
} from "@/api.types.v2";

/**
 * Delivery estimates: DispatchCenter.runSchedule editor.
 *
 * Week-grid layout (same visual language as the Scheduling screen): days as
 * columns, runs as colored chips. Runs with identical times across days are
 * ONE chip color / ONE edit target — click a chip to change its time or which
 * days it applies to, in a single edit.
 *
 * Runs are INTERNAL mixed-task trips. `departs` = load deadline (package must
 * be back at dispatch by then to ride the run); `returns` = trip back. These
 * drive the delivery estimates customers see.
 */

const WEEKDAYS: { key: RunScheduleWeekday; label: string }[] = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const EMPTY_WEEKLY: Record<RunScheduleWeekday, DispatchRun[]> = {
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
  sun: [],
};

/** Chip palette — mirrors the Scheduling screen legend style. Cycles if a
 *  dispatch somehow has more than 6 distinct run times. */
const GROUP_COLORS = [
  "bg-amber-100 text-amber-900 ring-amber-300",
  "bg-blue-100 text-blue-900 ring-blue-300",
  "bg-purple-100 text-purple-900 ring-purple-300",
  "bg-emerald-100 text-emerald-900 ring-emerald-300",
  "bg-rose-100 text-rose-900 ring-rose-300",
  "bg-cyan-100 text-cyan-900 ring-cyan-300",
];

/** Stable run id: generated once on add, preserved through edits. Identifies
 *  the run within a day for delivery estimates. */
function newRunId(day: RunScheduleWeekday): string {
  return `${day}-${Math.random().toString(36).slice(2, 8)}`;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

/** "12:00" → "12PM", "19:30" → "7:30PM" — compact chip labels. */
function compactTime(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const dh = h % 12 || 12;
  return m === 0
    ? `${dh}${period}`
    : `${dh}:${String(m).padStart(2, "0")}${period}`;
}

/** A "run group": the same (departs, returns) across one or more days. The
 *  unit users think in ("the noon run") and the unit they edit. */
interface RunGroup {
  key: string;
  departs: string;
  returns: string;
  days: RunScheduleWeekday[];
  /** Existing per-day run ids, preserved through edits. */
  idsByDay: Partial<Record<RunScheduleWeekday, string>>;
}

function groupRuns(
  weekly: Record<RunScheduleWeekday, DispatchRun[]>
): RunGroup[] {
  const map = new Map<string, RunGroup>();
  for (const { key: day } of WEEKDAYS) {
    for (const run of weekly[day]) {
      const key = `${run.departs}|${run.returns}`;
      let group = map.get(key);
      if (!group) {
        group = {
          key,
          departs: run.departs,
          returns: run.returns,
          days: [],
          idsByDay: {},
        };
        map.set(key, group);
      }
      group.days.push(day);
      group.idsByDay[day] = run.id;
    }
  }
  return [...map.values()].sort(
    (a, b) => toMinutes(a.departs) - toMinutes(b.departs)
  );
}

function expandGroups(
  groups: RunGroup[]
): Record<RunScheduleWeekday, DispatchRun[]> {
  const weekly: Record<RunScheduleWeekday, DispatchRun[]> = {
    mon: [],
    tue: [],
    wed: [],
    thu: [],
    fri: [],
    sat: [],
    sun: [],
  };
  for (const group of groups) {
    for (const day of group.days) {
      weekly[day].push({
        id: group.idsByDay[day] ?? newRunId(day),
        departs: group.departs,
        returns: group.returns,
      });
    }
  }
  for (const { key } of WEEKDAYS) {
    weekly[key].sort((a, b) => toMinutes(a.departs) - toMinutes(b.departs));
  }
  return weekly;
}

/** Draft being edited in the inline panel. */
interface RunDraft {
  /** Key of the group being edited; null = adding a new run. */
  originalKey: string | null;
  departs: string;
  returns: string;
  days: Set<RunScheduleWeekday>;
  /** Carried from the original group so per-day ids survive the edit. */
  idsByDay: Partial<Record<RunScheduleWeekday, string>>;
}

export function RunScheduleEditor({
  value,
  onChange,
}: {
  value: DispatchRunSchedule | null;
  onChange: (value: DispatchRunSchedule | null) => void;
}) {
  const [overrideDate, setOverrideDate] = useState("");
  const [draft, setDraft] = useState<RunDraft | null>(null);

  if (!value) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
        <div className="flex items-center gap-2 text-amber-800">
          <TriangleAlert className="h-4 w-4 shrink-0" />
          <span className="text-xs font-medium">
            Delivery estimates are OFF for this dispatch
          </span>
        </div>
        <p className="text-[11px] text-amber-700">
          Without a run schedule, customers see plain "N business days" instead
          of time estimates. Set up the internal trip schedule to enable them.
          This does not control which pickup dates customers can book.
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            onChange({ weekly: { ...EMPTY_WEEKLY }, overrides: {} })
          }
        >
          <Plus className="h-3 w-3 mr-1" />
          Set up run schedule
        </Button>
      </div>
    );
  }

  const groups = groupRuns(value.weekly);
  const colorOf = (groupKey: string) => {
    const index = groups.findIndex((g) => g.key === groupKey);
    return GROUP_COLORS[Math.max(index, 0) % GROUP_COLORS.length];
  };

  const startAdd = (day: RunScheduleWeekday) => {
    setDraft({
      originalKey: null,
      departs: "12:00",
      returns: "20:00",
      days: new Set([day]),
      idsByDay: {},
    });
  };

  const startEdit = (group: RunGroup) => {
    setDraft({
      originalKey: group.key,
      departs: group.departs,
      returns: group.returns,
      days: new Set(group.days),
      idsByDay: { ...group.idsByDay },
    });
  };

  const commitDraft = () => {
    if (!draft || draft.days.size === 0) return;
    const remaining = groups.filter((g) => g.key !== draft.originalKey);
    const edited: RunGroup = {
      key: `${draft.departs}|${draft.returns}`,
      departs: draft.departs,
      returns: draft.returns,
      days: WEEKDAYS.map((d) => d.key).filter((d) => draft.days.has(d)),
      idsByDay: draft.idsByDay,
    };
    onChange({ ...value, weekly: expandGroups([...remaining, edited]) });
    setDraft(null);
  };

  const deleteDraftGroup = () => {
    if (!draft) return;
    const remaining = groups.filter((g) => g.key !== draft.originalKey);
    onChange({ ...value, weekly: expandGroups(remaining) });
    setDraft(null);
  };

  const toggleDraftDay = (day: RunScheduleWeekday) => {
    if (!draft) return;
    const days = new Set(draft.days);
    if (days.has(day)) days.delete(day);
    else days.add(day);
    setDraft({ ...draft, days });
  };

  const addOverride = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(overrideDate)) return;
    onChange({
      ...value,
      overrides: { ...value.overrides, [overrideDate]: [] },
    });
    setOverrideDate("");
  };

  const removeOverride = (date: string) => {
    const overrides = { ...value.overrides };
    delete overrides[date];
    onChange({ ...value, overrides });
  };

  return (
    <div className="space-y-3">
      {groups.length === 0 && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-800">
          <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
          <span className="text-[11px]">
            No runs configured — estimates stay off until at least one day has a
            run.
          </span>
        </div>
      )}

      {/* Legend */}
      {groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] text-muted-foreground">Runs:</span>
          {groups.map((group) => (
            <button
              key={group.key}
              type="button"
              onClick={() => startEdit(group)}
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${colorOf(group.key)}`}
            >
              {compactTime(group.departs)}–{compactTime(group.returns)}
            </button>
          ))}
        </div>
      )}

      {/* Week grid — same visual language as the Scheduling screen */}
      <div
        className="grid grid-cols-7 gap-1 rounded-md border p-1"
        role="grid"
        aria-label="Weekly run schedule"
      >
        {WEEKDAYS.map(({ key, label }) => (
          <div
            key={key}
            className="text-center text-[11px] font-medium text-muted-foreground py-1"
          >
            {label}
          </div>
        ))}
        {WEEKDAYS.map(({ key, label }) => {
          const dayGroups = groups.filter((g) => g.days.includes(key));
          return (
            <div
              key={key}
              className="min-h-[72px] rounded bg-muted/40 border border-dashed border-border p-1 flex flex-col gap-1"
            >
              {dayGroups.length === 0 && (
                <span className="text-[9px] text-muted-foreground text-center pt-1">
                  No runs
                </span>
              )}
              {dayGroups.map((group) => (
                <button
                  key={group.key}
                  type="button"
                  onClick={() => startEdit(group)}
                  className={`w-full rounded px-1 py-1 text-[10px] font-medium ring-1 ring-inset text-left leading-tight ${colorOf(group.key)}`}
                  aria-label={`${label} run ${compactTime(group.departs)} to ${compactTime(group.returns)}`}
                >
                  {compactTime(group.departs)}–{compactTime(group.returns)}
                </button>
              ))}
              <button
                type="button"
                onClick={() => startAdd(key)}
                className="mt-auto w-full rounded border border-dashed border-border py-0.5 text-[9px] text-muted-foreground hover:bg-muted"
                aria-label={`Add run on ${label}`}
              >
                + Add
              </button>
            </div>
          );
        })}
      </div>

      {/* Inline edit panel */}
      {draft && (
        <div className="rounded-md border bg-muted/40 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">
              {draft.originalKey ? "Edit run" : "New run"}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              onClick={() => setDraft(null)}
              aria-label="Close run editor"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label className="text-[10px]">Departs (load deadline)</Label>
              <Input
                type="time"
                className="h-8 w-28 text-xs"
                value={draft.departs}
                onChange={(e) =>
                  setDraft({ ...draft, departs: e.target.value })
                }
                aria-label="Run departs"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px]">Returns</Label>
              <Input
                type="time"
                className="h-8 w-28 text-xs"
                value={draft.returns}
                onChange={(e) =>
                  setDraft({ ...draft, returns: e.target.value })
                }
                aria-label="Run returns"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-[10px]">Runs on these days</Label>
            <div className="flex gap-1">
              {WEEKDAYS.map(({ key, label }) => {
                const checked = draft.days.has(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleDraftDay(key)}
                    aria-pressed={checked}
                    aria-label={`Run on ${label}`}
                    className={`h-8 w-9 rounded text-[11px] font-medium ring-1 ring-inset transition-colors ${
                      checked
                        ? "bg-primary text-primary-foreground ring-primary"
                        : "bg-background text-muted-foreground ring-border hover:bg-muted"
                    }`}
                  >
                    {label[0]}
                  </button>
                );
              })}
            </div>
            {draft.days.size === 0 && (
              <p className="text-[10px] text-destructive">
                Pick at least one day.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between pt-1">
            {draft.originalKey ? (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-[11px] text-destructive hover:text-destructive"
                onClick={deleteDraftGroup}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete run
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={commitDraft}
                disabled={draft.days.size === 0}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground">
        Departs = load deadline (package must be at dispatch by then). Returns =
        trip back at dispatch. These runs control onward delivery estimates, not
        pickup availability. Click a run to change its time or days.
      </p>

      {/* Date overrides */}
      <div className="space-y-1.5 border-t pt-2">
        <Label className="text-xs">Date overrides (holidays / no runs)</Label>
        <div className="flex flex-wrap items-center gap-2">
          {Object.keys(value.overrides)
            .sort()
            .map((date) => (
              <Badge
                key={date}
                variant="secondary"
                className="text-[10px] gap-1"
              >
                <CalendarOff className="h-3 w-3" />
                {date}
                <button
                  type="button"
                  onClick={() => removeOverride(date)}
                  aria-label={`Remove override ${date}`}
                  className="ml-0.5"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </Badge>
            ))}
          <Input
            type="date"
            className="h-8 w-40 text-xs"
            value={overrideDate}
            onChange={(e) => setOverrideDate(e.target.value)}
            aria-label="Override date"
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-[11px]"
            onClick={addOverride}
            disabled={!overrideDate}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add no-run date
          </Button>
        </div>
      </div>

      <Button
        size="sm"
        variant="ghost"
        className="h-7 text-[11px] text-destructive hover:text-destructive"
        onClick={() => onChange(null)}
      >
        Remove schedule (disable estimates)
      </Button>
    </div>
  );
}
